import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Youtube, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

// Extract YouTube video ID
const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
    /youtube\.com\/shorts\/([^"&?\/\s]{11})/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// YouTube Video Card
function YouTubeVideoCard({ video, featured = false }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoId = extractYouTubeId(video.video_url);
  
  if (!videoId) return null;

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const thumbnailFallback = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  if (featured) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative rounded-2xl overflow-hidden bg-gray-900"
      >
        {!isPlaying ? (
          <>
            <div className="aspect-video relative">
              <img
                src={thumbnailUrl}
                alt={video.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = thumbnailFallback; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              
              {/* Play Button */}
              <button
                onClick={() => setIsPlaying(true)}
                className="absolute inset-0 flex items-center justify-center group"
              >
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl">
                  <Play className="h-10 w-10 text-white fill-white ml-1" />
                </div>
              </button>
              
              {/* Badge */}
              <Badge className="absolute top-4 left-4 bg-red-600 text-white">
                <Youtube className="h-3 w-3 mr-1" />
                Video Destacado
              </Badge>
            </div>
            
            {/* Info */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="font-heading text-2xl font-bold text-white mb-2 line-clamp-2">
                {video.title}
              </h3>
              {video.excerpt && (
                <p className="text-white/70 text-sm line-clamp-2">{video.excerpt}</p>
              )}
            </div>
          </>
        ) : (
          <div className="aspect-video">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
              title={video.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        )}
      </motion.div>
    );
  }

  // Smaller card version
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group cursor-pointer"
      onClick={() => setIsPlaying(true)}
    >
      {!isPlaying ? (
        <>
          <div className="aspect-video relative rounded-xl overflow-hidden mb-3">
            <img
              src={thumbnailUrl}
              alt={video.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={(e) => { e.target.src = thumbnailFallback; }}
            />
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center">
                <Play className="h-7 w-7 text-white fill-white ml-0.5" />
              </div>
            </div>
            <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm px-2 py-1 rounded text-xs text-white flex items-center gap-1">
              <Youtube className="h-3 w-3" />
            </div>
          </div>
          <h4 className="font-medium text-brand-text group-hover:text-primary transition-colors line-clamp-2 text-sm">
            {video.title}
          </h4>
        </>
      ) : (
        <div className="aspect-video rounded-xl overflow-hidden">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      )}
    </motion.div>
  );
}

export default function YouTubeSection() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/public/videos?limit=5`);
      setVideos(response.data.items || []);
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50" data-testid="youtube-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 aspect-video bg-gray-200 rounded-2xl" />
              <div className="space-y-4">
                <div className="aspect-video bg-gray-200 rounded-xl" />
                <div className="aspect-video bg-gray-200 rounded-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (videos.length === 0) {
    return null; // Don't show section if no videos
  }

  const [featuredVideo, ...otherVideos] = videos;

  return (
    <section className="py-16 bg-gray-50" data-testid="youtube-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <Youtube className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-brand-text">
                Nuestro Canal
              </h2>
              <p className="text-brand-textSecondary text-sm">
                Videos de Choluteca y Honduras
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://youtube.com/@cholutecanet"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full text-sm font-medium hover:bg-red-700 transition-colors"
            >
              <Youtube className="h-4 w-4" />
              Suscribirse
            </a>
            <Link to="/videos">
              <Button variant="outline" className="rounded-full">
                Ver todos
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Videos Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Featured Video */}
          <div className="lg:col-span-2">
            <YouTubeVideoCard video={featuredVideo} featured />
          </div>

          {/* Other Videos */}
          <div className="space-y-4">
            {otherVideos.slice(0, 2).map((video) => (
              <YouTubeVideoCard key={video.post_id} video={video} />
            ))}
          </div>
        </div>

        {/* More videos below on larger screens */}
        {otherVideos.length > 2 && (
          <div className="hidden lg:grid grid-cols-4 gap-6 mt-6">
            {otherVideos.slice(2, 6).map((video) => (
              <YouTubeVideoCard key={video.post_id} video={video} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
